import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { reachText, type Tier } from './AmbassadorTerms';

/** The program's terms from the partner settings, and the choices on the form (#713). */
interface AmbassadorProgram {
  open: boolean;
  commissionPct: number;
  commissionMonths: number;
  discountPct: number;
  discountMonths: number;
  holdDays: number;
  minPayoutCents: number;
  kinds: Record<string, string>;
  audiences: Record<string, string>;
  contact: string;
  /** The tier ladder (#902), lowest first, from Admin → Partners → Tiers. */
  tiers?: Tier[];
}

/** A code as it's saved: letters and numbers, upper case, 16 at most. */
const cleanCode = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
interface CodeCheck {
  code: string;
  available: boolean;
  error: string;
  suggestions: string[];
}
const dollars = (cents: number) => `$${cents % 100 ? (cents / 100).toFixed(2) : cents / 100}`;

/**
 * The public Brand Ambassadors page (#713): what homeschool creators, co-op leaders and reviewers get for sharing
 * ClassGuardian, and an application form (no account). Applications go to Admin → Partners for review.
 */
export function AmbassadorsPage() {
  const [program, setProgram] = useState<AmbassadorProgram | null>(null);
  const [showTiers, setShowTiers] = useState(false);
  const [f, setF] = useState({ name: '', email: '', link: '', otherLinks: '', kind: 'creator', followers: '', pitch: '', code: '', disclose: false });
  // Their code (#898), checked as they type: free, or taken with a few that are free.
  const [check, setCheck] = useState<CodeCheck | null>(null);
  useEffect(() => {
    const code = f.code;
    if (code.length < 3) {
      setCheck(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .get<CodeCheck>(`/api/ambassadors/code/${encodeURIComponent(code)}`)
        .then((r) => setCheck(r.code === code ? r : null))
        .catch(() => setCheck(null));
    }, 350);
    return () => clearTimeout(t);
  }, [f.code]);
  const codeOk = !!check && check.code === f.code && check.available;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  useEffect(() => {
    api
      .get<AmbassadorProgram>('/api/ambassadors')
      .then(setProgram)
      .catch(() => undefined);
  }, []);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });

  async function apply(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/api/ambassadors/apply', { ...f, followers: Math.max(0, parseInt(f.followers.replace(/[^0-9]/g, ''), 10) || 0) });
      setSent(true);
    } catch (err) {
      // Taken since they checked: show it on the code, with the suggestions.
      const e = err as Error & { body?: { field?: string; suggestions?: string[] } };
      if (e.body?.field === 'code') setCheck({ code: f.code, available: false, error: e.message, suggestions: e.body.suggestions ?? [] });
      else setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const p = program;
  const tiers = p?.tiers ?? [];
  return (
    <section className="amb">
      <div className="amb-pitch">
        <p className="landing-eyebrow">Brand Ambassadors</p>
        <h1>
          Share what works for your homeschool. <span className="landing-accent">Earn when families join.</span>
        </h1>
        <p className="landing-lead">For homeschool bloggers, creators, co-op leaders and curriculum reviewers who love ClassGuardian and want to share it with the families in their community.</p>
        <p className="muted">
          Already an ambassador? <Link to="/login">Sign in</Link> and open the Ambassador tab.
        </p>
        {/* No numbers (#900): each ambassador's rates come from their tier, told when they're approved. */}
        {/* One panel, a column per perk (#913): a small label, the perk, then the detail. */}
        <div className="amb-perks card">
          <div>
            <span className="amb-perk-label">You earn</span>
            <strong>A percentage of what families pay</strong>
            <span className="muted small">for their first months</span>
          </div>
          <div>
            <span className="amb-perk-label">They save</span>
            <strong>A discount for families who use your code</strong>
            <span className="muted small">at checkout</span>
          </div>
          {/* Their own family's no-cost access while they're active (#895, terms section 4). */}
          <div>
            <span className="amb-perk-label">Your family</span>
            <strong>ClassGuardian at no cost</strong>
            <span className="muted small">while you’re an active ambassador</span>
          </div>
          <div>
            <span className="amb-perk-label">You see it all</span>
            <strong>Every sign-up and payout in one place</strong>
            <span className="muted small">in your ClassGuardian account</span>
          </div>
        </div>
        {/* "tier" opens the tier table in place (#915), read live from Admin → Partners → Tiers. */}
        <p className="muted small">
          Your rates depend on your{' '}
          {tiers.length > 0 ? (
            <button type="button" className="link-btn amb-tier-link" aria-expanded={showTiers} aria-controls="amb-tiers" onClick={() => setShowTiers((v) => !v)}>
              tier
            </button>
          ) : (
            'tier'
          )}
          . We’ll tell you yours when you’re approved.
        </p>
        {showTiers && tiers.length > 0 && (
          <div id="amb-tiers" className="card amb-tiers">
            <div className="legal-table">
              <table>
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>You earn</th>
                    <th>Families get</th>
                    <th>Reached at</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t) => (
                    <tr key={t.name}>
                      <td>{t.name}</td>
                      <td>
                        {t.commissionPct}% for {t.commissionMonths} months
                      </td>
                      <td>
                        {t.discountPct}% off for {t.discountMonths} months
                      </td>
                      <td>{reachText(t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted small">
              Everyone starts on {tiers[0]?.name} and moves up automatically. Full details in section 6 of the{' '}
              <Link to="/ambassador-terms">Ambassador terms</Link>.
            </p>
          </div>
        )}
        <h2>How it works</h2>
        <ol className="amb-steps">
          <li>Apply here. We read every application and reply by email.</li>
          <li>Once you’re in, we place you in a tier, which sets what you earn and what your families get. You get your own code, a sign-up link and a Brand Ambassador page in your ClassGuardian account with your numbers.</li>
          <li>
            Share it.
            {p && ` Earnings are ready ${p.holdDays} days after each payment and paid out from ${dollars(p.minPayoutCents)}.`}
          </li>
        </ol>
      </div>

      <div className="card amb-form">
        {sent ? (
          <div className="stack">
            <h2>Thanks for applying!</h2>
            <p className="muted">We’ve emailed you a copy. We read every application and will reply within a few days.</p>
          </div>
        ) : p && !p.open ? (
          <div className="stack">
            <h2>Applications are closed for now</h2>
            <p className="muted">We aren’t adding new Brand Ambassadors at the moment. Please check back soon.</p>
          </div>
        ) : (
          <form className="stack" onSubmit={apply}>
            <h2>Apply to be a Brand Ambassador</h2>
            <div className="field-row wrap-row">
              <label className="grow">
                Your name
                <input value={f.name} onChange={(e) => set('name', e.target.value)} maxLength={80} required autoComplete="name" />
              </label>
              <label className="grow">
                Email
                <input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} maxLength={200} required autoComplete="email" />
              </label>
            </div>
            <label>
              Your code
              <input
                value={f.code}
                onChange={(e) => set('code', cleanCode(e.target.value))}
                maxLength={16}
                required
                autoComplete="off"
                spellCheck={false}
                className={`amb-code-input${check && check.code === f.code ? (check.available ? ' ok' : ' taken') : ''}`}
                aria-describedby="amb-code-help"
                placeholder="JORDAN"
              />
            </label>
            <div id="amb-code-help" className="stack tight" aria-live="polite">
              {f.code.length > 0 && f.code.length < 3 ? (
                <span className="muted small">A code is 3 to 16 letters or numbers.</span>
              ) : check && check.code === f.code ? (
                check.available ? (
                  <span className="small good-text">✓ {check.code} is available</span>
                ) : (
                  <>
                    <span className="small warn-text">{check.error}</span>
                    {check.suggestions.length > 0 && (
                      <span className="amb-code-sugs">
                        {check.suggestions.map((s) => (
                          <button key={s} type="button" className="chip" onClick={() => set('code', s)}>
                            {s}
                          </button>
                        ))}
                      </span>
                    )}
                  </>
                )
              ) : (
                <span className="muted small">Families type it when they sign up, so keep it short and easy to say.</span>
              )}
            </div>
            <label>
              Where you share (blog, channel or group)
              <input value={f.link} onChange={(e) => set('link', e.target.value)} maxLength={300} required placeholder="https://…" />
            </label>
            <label>
              Other links <span className="muted small">(optional)</span>
              <input value={f.otherLinks} onChange={(e) => set('otherLinks', e.target.value)} maxLength={500} placeholder="YouTube, TikTok, your co-op’s site…" />
            </label>
            <div className="field-row wrap-row">
              <label className="grow">
                You are a…
                <select value={f.kind} onChange={(e) => set('kind', e.target.value)}>
                  {Object.entries(p?.kinds ?? {}).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grow">
                Followers <span className="muted small">(your largest platform)</span>
                <input inputMode="numeric" value={f.followers} onChange={(e) => set('followers', e.target.value.replace(/[^0-9,]/g, ''))} maxLength={13} required placeholder="4,200" />
              </label>
            </div>
            <label>
              How would you share ClassGuardian?
              <textarea rows={4} value={f.pitch} onChange={(e) => set('pitch', e.target.value)} maxLength={2000} />
            </label>
            <label className="check">
              <input type="checkbox" checked={f.disclose} onChange={(e) => set('disclose', e.target.checked)} required />
              <span>
                I agree to the{' '}
                <a href="/ambassador-terms" target="_blank" rel="noreferrer">
                  Brand Ambassador terms
                </a>
                , including saying I’m a ClassGuardian Brand Ambassador whenever I share my link.
              </span>
            </label>
            {error && <p className="error">{error}</p>}
            <button className="btn primary big" type="submit" disabled={busy || !p || !codeOk}>
              {busy ? 'Sending…' : 'Send my application'}
            </button>
            <p className="muted small center">We reply within a few days. If you’re approved, we’ll set up your ClassGuardian account.</p>
          </form>
        )}
      </div>
    </section>
  );
}
